import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import ConfirmationModal from '@/components/Modal/ConfirmationModal/ConfirmationModal';
import Modal from '@/components/Modal/Modal';
import Table from '@/components/Tables/Table/Table';
import Tag from '@/components/Tag/Tag';
import { TagTypes } from '@/components/Tag/Tag.types';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import Tooltip from '@/components/Tooltip/Tooltip';
import { useDeleteStoreGroup } from '@/services/groups/groups.service';
import { useStores } from '@/services/stores/stores.service';
import { Store } from '@/types/stores.types';
import useConfirmationModal from '@/utils/useConfirmationModal/useConfirmationModal';
import { useQueryClient } from '@tanstack/react-query';
import { CellContext, RowSelectionState } from '@tanstack/react-table';
import { AxiosResponse } from 'axios';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../MyStores.module.css';
import AddStoresToGroup from './AddStoresToGroup/AddStoresToGroup';
import MoveStoresModal from './MoveStores/MoveStoresModal';
import { AddOrMoveButtonProps, StoreGroupTableProps } from './StoreGroups.types';
import { AlertTriangle } from 'react-feather';
import { Avatar, Chip, Grid } from '@mui/material';
import RequestProcessingModal from '@/components/Modal/RequestProcessingModal/RequestProcessingModal';
import { useRequestProcessingModal } from '@/hooks/useRequestProcessingModal';

const AddOrMoveButton: FC<AddOrMoveButtonProps> = ({
  selectedRows,
  handleMoveStores,
  handleAddStore,
  showWarningAlert,
}) => {
  const { t } = useTranslation();
  if (Object.values(selectedRows).includes(true))
    return (
      <Grid container direction="column" alignItems="flex-end">
        {showWarningAlert && (
          <Grid
            sx={{
              width: '100%',
            }}
          >
            <Chip
              avatar={
                <Avatar style={{ width: '35px', height: '35px', backgroundColor: '#FAE4D0', padding: '1px' }}>
                  <AlertTriangle color="#E6740A" />
                </Avatar>
              }
              data-testid="icon"
              key="emptyStore"
              label={t('moveStoresModal.groupDeletedWarning')}
              color="default"
              variant="outlined"
              sx={{
                fontSize: '14px',
                fontWeight: '500',
                textAlign: 'left',
                height: '40px',
                backgroundColor: '#FAE4D0',
                color: '#252525',
                borderColor: '#E6740A',
                width: '100%',
                justifyContent: 'left',
              }}
            />
          </Grid>
        )}
        <Grid>
          <Button variant={ButtonVariants.Text} onClick={handleMoveStores}>
            {t('storeGroups.moveSelectedStoresButton')}
          </Button>
        </Grid>
      </Grid>
    );
  return (
    <div className={styles.storeGroupTableButton}>
      <Button variant={ButtonVariants.Text} onClick={handleAddStore}>
        {t('storeGroups.addStoreToGroupButton')}
      </Button>
      <Tooltip content={t('storeGroups.addStoreToGroupTooltip')} />
    </div>
  );
};

const isAllRowsSelected = (selectedRows: RowSelectionState, data: { data: any[] } | undefined): boolean =>
  Object.values(selectedRows).length === data?.data.length;

const Status = ({ row }: CellContext<Store, unknown>) => {
  const { t } = useTranslation();
  if (row.original.status) {
    const label = TagTypes.Pending;
    return <Tag label={t(`tags.${label}`)} type={label} />;
  }
  return <p style={{ fontStyle: 'italic', fontSize: 12 }}>{t('storeGroups.noStatusLabel')}</p>;
};

const StoreGroupTable: FC<StoreGroupTableProps> = ({
  groupId,
  groupName,
  allGroups,
  editAccess,
  globalFreezeExists,
  setPartialSuccessData,
  confirmationModalIsOpen,
  setConfirmationModalIsOpen,
  setConfirmationModalIsTitle,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [moveStoresIsOpen, setMoveStoresIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

  const [addStoresIsOpen, setAddStoresIsOpen] = useState(false);
  const { mutate, isLoading: deleteIsLoading } = useDeleteStoreGroup(groupId);
  const { data, isLoading: storesIsLoading, isError } = useStores(true, groupId);
  const storesExist = data?.data !== undefined && data.data.length > 0;

  const { isModalOpen, modalType, showTimeoutModal, showErrorModal, handleCloseAndRefresh } = useRequestProcessingModal(
    { onRefresh: () => window.location.reload() },
  );

  const closeAddStores = () => {
    setAddStoresIsOpen(false);
  };

  const openDeleteModal = () => {
    setDeleteModalIsOpen(true);
  };
  const closeDeleteModal = () => {
    setDeleteModalIsOpen(false);
  };

  const closeConfirmationModal = () => {
    queryClient.invalidateQueries(['store-groups']);
    setSelectedRows({});
    setConfirmationModalIsOpen(false);
  };

  const onMoveSuccess = (response: AxiosResponse) => {
    const selectedStoresCount = Object.values(selectedRows).filter((val) => val).length;
    setMoveStoresIsOpen(false);
    setSelectedRows({}); // Clear selected rows when move is successful
    queryClient.invalidateQueries(['stores']);
    if (response.status === 207) {
      // if status is 207, there is a partial failure assigning one of the stores to the new group so we show a different modal
      setPartialSuccessData({
        storesWithErrors: response.data.storeDetails.filter(
          (detail: { status: string; errorMessage: string; storeNo: string }) => detail.status === 'FAILED',
        ),
        storesSubmittedCount: selectedStoresCount,
      });
    } else {
      setConfirmationModalIsTitle(t('storeGroups.confirmationTitles.requestSent'));
      setConfirmationModalIsOpen(true);
    }
  };

  const handleDeleteStoreGroup = () => {
    mutate(undefined, {
      onSuccess: () => {
        setConfirmationModalIsTitle(t('storeGroups.confirmationTitles.groupRemoved', { groupName }));
        setConfirmationModalIsOpen(true);
        setDeleteModalIsOpen(false);
      },
      onError: (error: unknown) => {
        closeDeleteModal();
        if (error instanceof Error && error.message === 'API Gateway request timed out') {
          showTimeoutModal();
          return;
        }
        // Notify the user via toast per UX and test expectations
        showToast({
          toastType: ToastTypes.Error,
          id: `${groupId}-deleteGroupError`,
          label: t('error.deletingStoreGroup'),
        });
        showErrorModal();
      },
    });
  };
  const handleAddStore = () => {
    setConfirmationModalIsTitle(t('storeGroups.confirmationTitles.requestSent', { groupName }));
    setAddStoresIsOpen(true);
  };
  const handleMoveStores = () => {
    setMoveStoresIsOpen(true);
  };

  if (isError) {
    showToast({ toastType: ToastTypes.Error, id: `${groupId}StoresError`, label: t('error.fetchingStoresForGroup') });
  }

  return (
    <div className={styles.storeGroupTable}>
      {editAccess && !globalFreezeExists && (
        <AddOrMoveButton
          selectedRows={selectedRows}
          handleAddStore={handleAddStore}
          handleMoveStores={handleMoveStores}
          showWarningAlert={isAllRowsSelected(selectedRows, data)}
        />
      )}
      {storesExist ? (
        <Table
          enableRowSelection={editAccess && !globalFreezeExists}
          selectedRows={selectedRows}
          onRowSelectionChange={setSelectedRows}
          isLoading={storesIsLoading}
          loadingRowCount={5}
          data={data?.data || []}
          columns={[
            { header: t('storeGroups.table.storeId'), accessorKey: 'storeNo' },
            {
              header: t('storeGroups.table.location'),
              accessorFn: (row: Store) => `${row.address}, ${row.city} ${row.state} ${row.zip}`,
            },
            { header: t('storeGroups.table.status'), accessorKey: 'status', cell: Status },
          ]}
        />
      ) : (
        <div className={styles.noRowsMessage}>
          <p>{t('storeGroups.noStoresInGroupMessage')}</p>
          {editAccess && (
            <Button variant={ButtonVariants.Text} type="button" onClick={openDeleteModal}>
              {t('storeGroups.removeGroupButton')}
            </Button>
          )}
        </div>
      )}

      <MoveStoresModal
        isOpen={moveStoresIsOpen}
        onClose={() => setMoveStoresIsOpen(false)}
        selectedStores={data?.data.filter((store, index) => selectedRows[index]) || []}
        currentGroup={groupName}
        allGroups={allGroups}
        onSuccess={onMoveSuccess}
        currentGroupId={groupId}
        isDeleteGroup={isAllRowsSelected(selectedRows, data)}
        onRefresh={() => window.location.reload()}
      />
      <AddStoresToGroup
        setPartialSuccessData={setPartialSuccessData}
        isOpen={addStoresIsOpen}
        onClose={closeAddStores}
        setConfirmationModalVisible={() => setConfirmationModalIsOpen(true)}
        groupId={groupId}
        allGroups={allGroups}
        groupName={groupName}
      />
      <Modal
        isOpen={deleteModalIsOpen}
        onClose={closeDeleteModal}
        headerContent={<div className={styles.deleteHeader}>{t('removeGroupModal.header', { groupName })}</div>}
        footerContent={
          <div className={styles.deleteGroupFooter}>
            <div className={styles.deleteButtons}>
              <Button variant={ButtonVariants.Secondary} onClick={closeDeleteModal} type="button">
                {t('common.cancel')}
              </Button>
              <Button onClick={handleDeleteStoreGroup} isLoading={deleteIsLoading} type="button">
                {t('common.confirm')}
              </Button>
            </div>
          </div>
        }
      />
      {isModalOpen && <RequestProcessingModal isOpen={isModalOpen} type={modalType} onClose={handleCloseAndRefresh} />}
    </div>
  );
};

export default StoreGroupTable;
